import { ModalBaseProps } from '@round/ui-kit';
import { FullscreenModal, assets } from '@round/whitelabel-ui';
import SongData from './SongData/SongData';
import styles from './SongDataModal.module.css';
import { useState } from 'react';
import TiktokData from './Tiktok/TiktokData';
import SongIndicators from './components/Indicators/Indicators';
import SongCharts from './SongCharts/SongCharts';
import { Platform, PlatformFiltersProvider } from './PlatformFilters/usePlatformFilters';
import PlatformIcon from 'Modules/Plans/components/PlatformIcon';
import InstagramData from './Instagram/InstagramData';
import { getMergedAudioIdsFromSongData } from './helpers';
import cn from 'classnames';
import { IndexTabs, TabPanel } from '@round/whitelabel-ui';
import { useSong, useMusicSong } from '@round/creatorbase-helpers/api-hooks';

const { Cross: CloseIcon } = assets;

type Props = Pick<ModalBaseProps, 'isOpen' | 'onClose'> & {
    songId: number | undefined | null;
};

type PlatformTabs = 'tiktok' | 'instagram';

const SongDataModal = ({ isOpen, onClose, songId }: Props) => {
    const [activePlatform, setActivePlatform] = useState<PlatformTabs>('tiktok');

    const { data: song, isLoading: isSongLoading } = useSong(songId);
    const { data: musicSong, isLoading: isMusicSongLoading } = useMusicSong(song?.music_song_id);

    const { tiktokAudioIds, instagramAudioIds } = getMergedAudioIdsFromSongData(song, musicSong);

    return (
        <FullscreenModal isOpen={isOpen} onClose={onClose} closeOnOverlayClick closeOnEscape>
            <div className={styles.header}>
                <SongData key={songId} songId={songId} />

                <button className={styles.closeButton} onClick={onClose}>
                    <CloseIcon />
                </button>
            </div>

            <PlatformFiltersProvider
                availablePlatforms={
                    isSongLoading || isMusicSongLoading
                        ? []
                        : ([
                              ...(tiktokAudioIds.length ? ['tiktok'] : []),
                              ...(instagramAudioIds.length ? ['instagram'] : []),
                          ] as Platform[])
                }
            >
                <div className={cn(styles.indicatorsContainer, styles.content)}>
                    <SongIndicators songId={songId} />
                </div>
                <SongCharts songId={songId} className={cn(styles.content, styles.charts)} />
            </PlatformFiltersProvider>

            <IndexTabs activeTab={activePlatform} onChange={(platform) => setActivePlatform(platform)}>
                <IndexTabs.Menu className={styles.platformTabContainer}>
                    <IndexTabs.Item name="tiktok">
                        <PlatformIcon platform="tiktok" colored className={styles.platformIcon} />
                        <span>TikTok Audio</span>
                    </IndexTabs.Item>
                    <IndexTabs.Item name="instagram">
                        <PlatformIcon platform="instagram" colored className={styles.platformIcon} />
                        <span>Instagram Audio</span>
                    </IndexTabs.Item>
                </IndexTabs.Menu>

                <div className={styles.content}>
                    <TabPanel name="tiktok">
                        <TiktokData songId={songId} />
                    </TabPanel>
                    <TabPanel name="instagram">
                        <InstagramData songId={songId} />
                    </TabPanel>
                </div>
            </IndexTabs>
        </FullscreenModal>
    );
};

export default SongDataModal;
